import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { saveStudentWriting, saveLessonProgress } from '../../firestoreFunctions';
import { auth, db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'; // Added updateDoc
import './LessonModules.css';
import { checkLessonAccess } from '../../utils/checkLessonAccess';
import PodcastPlayer from "../PodcastPlayer";
import { saveLastLocation } from '../../utils/lastLocation';

const LessonWriting = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [checkingAccess, setCheckingAccess] = useState(true);
  const [warning, setWarning] = useState('');
  const [lessonTitle, setLessonTitle] = useState('');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [writingPrompt, setWritingPrompt] = useState(null);
  const [studentWriting, setStudentWriting] = useState('');
  const [finalFeedback, setFinalFeedback] = useState('');
  const [loading, setLoading] = useState(false);

  const [recallAnswers, setRecallAnswers] = useState({});
  const [showListening, setShowListening] = useState(false);
  const [hintsUsed, setHintsUsed] = useState({}); 
const [clozeMistakes, setClozeMistakes] = useState({});

  const [timeLeft, setTimeLeft] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  useEffect(() => {
    saveLastLocation(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const verifyAccess = async () => {
      const lessonNum = parseInt(lessonId);
      const allowed = await checkLessonAccess(lessonNum);
      if (!allowed) {
        setWarning('🚫 This lesson is locked. Redirecting you to your dashboard...');
        setTimeout(() => navigate('/dashboard'), 2500);
      }
      setCheckingAccess(false);
    };
    verifyAccess();
  }, [lessonId, navigate]);

  useEffect(() => {
    const fetchWritingData = async () => {
      try {
        const response = await fetch(`/data/lesson${lessonId}-writing.json`);
        if (!response.ok) throw new Error('Writing data not found');
        const data = await response.json();
        setLessonTitle(data.title);
        setBackgroundImage(data.backgroundImage);
        setWritingPrompt(data.writingPrompt);

        const userId = auth.currentUser?.uid;
        if (userId) {
          const lid = String(lessonId).startsWith('lesson') ? lessonId : `lesson${lessonId}`;
          const writingDocRef = doc(db, 'users', userId, 'writings', lid);
          const writingSnap = await getDoc(writingDocRef);

          if (writingSnap.exists()) {
            const writingData = writingSnap.data();
            setStudentWriting(writingData.writingText || '');
            setFinalFeedback(writingData.feedback || '');
            setWordCount(countWords(writingData.writingText || ''));
            
            // 🔹 THE FIX: Check if they completed Phase 1 in the past
            if (writingData.vocabRecall?.status === 'completed') {
              setRecallAnswers(writingData.vocabRecall.answers || {});
              setShowListening(true); // Automatically jump to Phase 2
            }
          }
        }
      } catch (error) { console.error('❌ Error loading data:', error); }
    };

    fetchWritingData();
  }, [lessonId]);

  useEffect(() => {
    if (!timerRunning || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setTimerRunning(false);
          alert('⏰ Time is up!');
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerRunning, timeLeft]);

  const countWords = (text) => text.trim().split(/\s+/).filter(w => w.length > 0).length;

  const handleWritingChange = (e) => {
    const text = e.target.value;
    setStudentWriting(text);
    setWordCount(countWords(text));
  };

  const handleRecallChange = (wordKey, value) => {
  const suffix = wordKey; // Since we use suffix as key
  const isCorrect = value.trim().toLowerCase() === suffix.toLowerCase();
  
  // 🔹 If they typed a full word and it's wrong, record a mistake
  if (value.length >= suffix.length && !isCorrect) {
    setClozeMistakes(prev => ({
      ...prev,
      [wordKey]: (prev[wordKey] || 0) + 1
    }));
  }

  setRecallAnswers(prev => ({
    ...prev,
    [wordKey]: value.trim()
  }));
};

  // 🔹 REFINED LOGIC: Logic to determine if task is finished
  const checkCompletionStatus = () => {
    if (!writingPrompt?.paragraphs) return { correctCount: 0, totalRequired: 0, isTaskFinished: false };
    
    const allMatches = writingPrompt.paragraphs.join(' ').match(/\w+\[(\w+)\]/g) || [];
    const totalRequired = allMatches.length;
    
    let correctCount = 0;
    allMatches.forEach((match) => {
      const parts = match.match(/(\w+)\[(\w+)\]/);
      const suffix = parts[2].toLowerCase();
      if (recallAnswers[suffix]?.toLowerCase() === suffix) {
        correctCount++;
      }
    });

    return { 
      correctCount, 
      totalRequired, 
      isTaskFinished: totalRequired > 0 && correctCount === totalRequired 
    };
  };

  const { correctCount, totalRequired, isTaskFinished } = checkCompletionStatus();

  const renderRecallParagraph = (para) => {
    const parts = para.split(/(\w+\[\w+\])/g);

    return para.split(/(\w+\[\w+\])/g).map((part, index) => {
  const match = part.match(/(\w+)\[(\w+)\]/);
  
  if (match) {
    const prefix = match[1];
    const suffix = match[2];
    const wordKey = suffix.toLowerCase();
    const userInput = recallAnswers[wordKey] || '';
    const isCorrect = userInput.toLowerCase() === suffix.toLowerCase();
    const hintCount = hintsUsed[wordKey] || 0;
    const hintText = suffix.substring(0, hintCount);

    return (
      <span key={index} style={styles.clozeWrapper}>
        <span style={styles.prefixText}>{prefix}</span>
        <span style={{ position: 'relative', display: 'inline-block' }}>
          <input
            type="text"
            maxLength={suffix.length}
            autoComplete="off"  // 🔹 Add this
  autoCorrect="off"   // 🔹 Add this
  spellCheck="false"
            style={{
              ...styles.clozeInput,
              /* 🔹 FIX: Use 'ch' for perfect character-based width */
              width: `${suffix.length + 1.5}ch`, 
              minWidth: '3ch',
              backgroundColor: isCorrect ? '#d1fae5' : (userInput.length > 0 ? '#eff6ff' : '#fff'),
              borderColor: isCorrect ? '#10b981' : '#3b82f6',
              textAlign: 'center',
              transition: 'width 0.2s ease', // Smoothly adjusts if needed
            }}
            value={isCorrect ? suffix : userInput}
            onChange={(e) => handleRecallChange(wordKey, e.target.value)}
            // Show hint letters followed by dots for remaining letters
            placeholder={hintText + ".".repeat(Math.max(0, suffix.length - hintCount))}
            disabled={isCorrect}
          />
          
          {/* Hint Button Logic */}
          {!isCorrect && hintCount < suffix.length && !userInput && (
            <button
              onClick={() => setHintsUsed({ ...hintsUsed, [wordKey]: hintCount + 1 })}
              style={{
                ...styles.hintBtn,
                position: 'absolute',
                right: '-18px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              <span role="img" aria-label="hint">💡</span>
            </button>
          )}
        </span>
      </span>
    );
  }
  return <span key={index}>{part}</span>;
})}

const handleUnlockListening = async () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    setLoading(true);
    try {
      const lid = String(lessonId).startsWith('lesson') ? lessonId : `lesson${lessonId}`;
     const fullTextSnapshot = writingPrompt.paragraphs.join('\n'); // The text with [suffixes]
      const progressData = {
    vocabRecall: {
      answers: recallAnswers,
      mistakeCount: clozeMistakes, // 🔹 NEW: Record how many times they failed
      hintsCount: hintsUsed,       // 🔹 NEW: Record how many hints they took
      originalText: writingPrompt.paragraphs.join('\n'),
      status: 'completed'
        }
      };

      const writingDocRef = doc(db, 'users', userId, 'writings', lid);
      
      // 🔹 Use setDoc with merge: true to ensure it always saves
      await setDoc(writingDocRef, progressData, { merge: true });

      setShowListening(true);
    } catch (err) {
      console.error("❌ Failed to save vocab progress:", err);
      setShowListening(true); 
    } finally {
      setLoading(false);
    }
};
 const handleSubmitWriting = async () => {
    if (!studentWriting.trim()) return alert('Please write something.');
    setLoading(true);
    
    try {
      const userId = auth.currentUser?.uid;
      if (userId) {
        // 1. Save the essay content for the teacher to see
        await saveStudentWriting(userId, `lesson${lessonId}`, studentWriting);

        // 2. 🔹 Mark the WHOLE lesson as finished in the database
        // This is what unlocks the next lesson on the dashboard
        await saveLessonProgress(userId, `lesson${lessonId}`, { 
          writingCompleted: true,
          completedAt: new Date() 
        });

        alert('✅ Writing submitted! You have officially completed this lesson.');
      }
    } catch (error) { 
      console.error("❌ Error during final submission:", error);
      alert("Something went wrong saving your progress.");
    } finally { 
      setLoading(false); 
    }
  };

  const startTimer = () => { setTimeLeft(15 * 60); setTimerRunning(true); };
  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec < 10 ? '0' : ''}${sec}`;
  };

  if (checkingAccess) return <div>Checking access...</div>;
  if (warning) return <div style={{ textAlign: 'center', color: 'crimson', marginTop: '50px' }}>{warning}</div>;

  return (
    <div className="lesson-container" style={styles.mainWrapper}>
      <div 
        className="hero-section" 
        style={{ 
          ...styles.hero, 
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${backgroundImage})` 
        }}
      >
        <h1 style={styles.heroTitle}>{lessonTitle}</h1>
      </div>

      <div className="writing-content-area" style={styles.contentPadding}>
        <div style={styles.card}>
          <h2 style={styles.sectionHeader}>Phase 1: Vocabulary Recall</h2>
          <p style={styles.instructionText}>
            Type the missing letters for the key academic words to unlock the next phase.
            <br />
            <span style={styles.hintNote}>
              *Clear the box to see the <span role="img" aria-label="hint">💡</span> hint button.
            </span>
          </p>

          <div style={styles.recallBox}>
            {writingPrompt?.paragraphs?.map((para, index) => (
              <div key={index} style={styles.paragraphText}>
                {renderRecallParagraph(para)}
              </div>
            ))}
          </div>

          {!showListening && (
            <button 
              onClick={handleUnlockListening}
              style={{
                ...styles.unlockButton,
                opacity: isTaskFinished ? 1 : 0.5,
                cursor: isTaskFinished ? 'pointer' : 'not-allowed',
                backgroundColor: isTaskFinished ? '#10b981' : '#1e3a8a'
              }}
              disabled={!isTaskFinished || loading}
            >
              {loading ? "Saving..." : (isTaskFinished ? "Unlock Listening & Writing →" : `Complete All Words (${correctCount}/${totalRequired})`)}
            </button>
          )}
        </div>

        {showListening && (
          <div style={styles.card}>
            <h2 style={styles.sectionHeader}>Phase 2: Listen to the Podcast. Take notes!</h2>
            {writingPrompt?.audioUrl && (
              <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
                <PodcastPlayer src={writingPrompt.audioUrl} artwork="/images/hostsimage.png" accent="#0ea5e9" />
              </div>
            )}
            <h3 style={styles.instructionText}>{writingPrompt?.instructions}</h3>
          </div>
        )}

        {showListening && (
          <div style={styles.card}>
            <h2 style={styles.sectionHeader}>Phase 3: Integrated Writing</h2>
            {!timerRunning ? (
              <button onClick={startTimer} style={styles.startButton}>Start 15-Minute Timer</button>
            ) : (
              <div style={styles.timerDisplay}>Time Left: {formatTime(timeLeft)}</div>
            )}

            <textarea
              value={studentWriting}
              onChange={handleWritingChange}
              rows="15"
              placeholder="Synthesize information from the reading and listening to write a well-organized essay. Focus on clarity, coherence, and using evidence from both sources (Phase 1 and Phase 2)."
              style={styles.textArea}
              disabled={timerRunning && timeLeft === 0}
            />

            <div style={styles.wordCount}>Word Count: {wordCount}</div>
            <button onClick={handleSubmitWriting} style={styles.submitButton} disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Writing'}
            </button>
          </div>
        )}

        {finalFeedback && (
          <div style={styles.feedbackBox}>
            <h3>Your Teacher's Feedback</h3>
            <div dangerouslySetInnerHTML={{ __html: finalFeedback }} style={styles.feedbackContent} />
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <button 
  style={styles.finishBtn} 
  onClick={async () => {
    const userId = auth.currentUser?.uid;
    // Save current writing draft before leaving
    if (userId && studentWriting.length > 0) {
      await saveStudentWriting(userId, `lesson${lessonId}`, studentWriting);
    }
    navigate(`/dashboard`);
  }}
>
  TAKE A BREAK & GO TO DASHBOARD
</button>
      </div>
      <div style={styles.navigationButtons}>
        <button style={styles.navButton} onClick={() => navigate(`/lesson/${lessonId}/reading`)}>← Back to Reading</button>
      </div>
    </div>
  );
};

export default LessonWriting;

const styles = {
  mainWrapper: { backgroundColor: '#f3f4f6', minHeight: '100vh' },
  hero: { height: '350px', backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', textAlign: 'center' },
  heroTitle: { fontSize: '3.5rem', fontWeight: '800', fontFamily: 'Merriweather, serif', textShadow: '2px 4px 10px rgba(0,0,0,0.3)', maxWidth: '900px' },
  contentPadding: { maxWidth: '1000px', margin: '0 auto', padding: '40px 20px' },
  card: { backgroundColor: '#fff', padding: '30px', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', marginBottom: '30px' },
  sectionHeader: { fontFamily: 'Merriweather, serif', color: '#1e3a8a', marginBottom: '15px' },
  instructionText: { color: '#666', marginBottom: '20px', fontSize: '1.1rem' },
  recallBox: { lineHeight: '2.5', fontSize: '1.15rem', color: '#333', marginBottom: '30px', padding: '25px', backgroundColor: '#f8fafc', borderRadius: '12px' },
  paragraphText: { marginBottom: '15px' },
  clozeWrapper: { display: 'inline-flex', alignItems: 'center', marginRight: '40px', marginBottom: '10px' },
  prefixText: { fontWeight: '800', color: '#1e3a8a', fontSize: '1.2rem' },
  clozeInput: { border: '2px solid', borderRadius: '6px', outline: 'none', padding: '2px 8px', textAlign: 'left', fontSize: '1.1rem', fontWeight: '600', transition: 'all 0.3s ease' },
  hintBtn: { position: 'absolute', right: '-35px', top: '50%', transform: 'translateY(-50%)', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', zIndex: 5 },
  hintNote: { fontSize: '0.85rem', color: '#1e3a8a', fontStyle: 'italic', display: 'block', marginTop: '5px', opacity: 0.8 },
  unlockButton: { padding: '16px 32px', backgroundColor: '#1e3a8a', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', transition: 'all 0.3s' },
  textArea: { width: '100%', padding: '20px', fontSize: '1.1rem', borderRadius: '10px', border: '1px solid #ddd', fontFamily: 'Georgia, serif', lineHeight: '1.6' },
  timerDisplay: { fontSize: '24px', fontWeight: 'bold', color: '#e11d48', marginBottom: '15px' },
  startButton: { padding: '12px 24px', backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', marginBottom: '20px', cursor: 'pointer', fontWeight: 'bold' },
  submitButton: { padding: '15px 30px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1.1rem', fontWeight: 'bold' },
  wordCount: { textAlign: 'right', color: '#666', marginTop: '5px', marginBottom: '15px' },
  feedbackBox: { backgroundColor: '#eff6ff', padding: '25px', borderRadius: '12px', border: '1px solid #bfdbfe' },
  navigationButtons: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px', padding: '40px 0' },
  navButton: { width: '300px', padding: '12px', backgroundColor: '#1e3a8a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' },
  finishBtn: { backgroundColor: '#1e3a8a', color: '#fff', padding: '15px 40px', borderRadius: '50px', border: 'none', fontWeight: 'bold', fontSize: '1.1rem', cursor: 'pointer' }
};